import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Col, Space, Typography, message } from 'antd';
import { useHistory } from 'react-router-dom';
import { usePermify } from '@permify/react-role';
import { useMutation } from '@apollo/react-hooks';
import StatusTag, { getServedImpressions } from '../Campaigns/shared/StatusTag';
import { canEditCampaignOrder } from '../Campaigns/shared/editAccess';
import { useCampaignRequestorContext } from '../../../core/components/campaignRequestor/useCampaignRequestorContext';
import { getCampaignOrderErrorMessage } from '../../../core/utils/constants/campaignOrderErrorType';
import { markDeliverableLineItems } from './CampaignRequestorUtil';
import { isPercentageModel } from '../../../core/utils/pricing/cpmHelper';
import UPDATE_LINE_ITEM_MARKED_FOR_DELIVERY from '../../../core/GraphQl/Mutations/UPDATE_LINE_ITEM_MARKED_FOR_DELIVERY';
import './styles.css';

const { Text } = Typography;

const CampaignRequestorReviewSummary = ({
  formValues,
  currentCampaignOrder,
  editable = false
}) => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);
  const {
    updateCampaignOrder,
    selectedAdvertiser,
  } = useCampaignRequestorContext();

  // Post-launch edit access is processor-only (shared/editAccess.js)
  const { isAuthorized } = usePermify();
  const [isProcessor, setIsProcessor] = useState(false);
  const [isAdOps, setIsAdOps] = useState(false);
  useEffect(() => {
    const checkPermission = async () => {
      setIsProcessor(await isAuthorized(['isProcessor'], []));
      setIsAdOps(await isAuthorized(['isTrafficker', 'isProcessor'], []));
    };
    checkPermission();
  }, [isAuthorized]);

  const [updateLineItemMarkedForDelivery] = useMutation(UPDATE_LINE_ITEM_MARKED_FOR_DELIVERY);

  // Unknown/unsaved status behaves like DRAFT (editable)
  const canEdit = canEditCampaignOrder(currentCampaignOrder?.status || 'DRAFT', { isProcessor });

  // Cleanup function to prevent state updates on unmounted component
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const campaignName = formValues?.name || '';
  const advertiser = formValues?.advertiser || currentCampaignOrder?.advertiser || selectedAdvertiser;

  const handleMakeEdits = () => {
    if (currentCampaignOrder?.id) {
      history.push(`/campaigns/requester/edit/${currentCampaignOrder.id}`);
    }
  };

  const handleSave = async () => {
    // Only save if we have an existing campaign order (review mode should always have one)
    if (!currentCampaignOrder?.id) {
      message.warning('No campaign order to save');
      return;
    }

    try {
      if (!isMountedRef.current) return;
      setLoading(true);
      message.loading('Saving budget changes...', 0);

      const formCampaigns = formValues.campaigns || [];
      const existingCampaigns = currentCampaignOrder?.campaigns || [];

      // Only update if we have campaigns to update
      if (existingCampaigns.length === 0 || formCampaigns.length === 0) {
        message.destroy();
        message.info('No changes to save');
        return;
      }

      // Build minimal update payload - update all campaigns and their line items
      const campaignUpdates = [];

      existingCampaigns.forEach((existingCampaign, campaignIndex) => {
        const formCampaign = formCampaigns[campaignIndex];

        if (!existingCampaign.id || !formCampaign) {
          return; // Skip if campaign doesn't exist in both
        }

        const lineItems = formCampaign.lineItems || [];

        // Build line item updates for this campaign
        const lineItemUpdates = lineItems
          .filter(lineItem => lineItem.id) // Only update existing line items
          .map(lineItem => ({
            where: { id: lineItem.id },
            data: {
              budgetTotal: Number(lineItem.budgetTotal || 0),
            }
          }));

        // Build campaign update payload
        const campaignUpdateData = {
          // Always update campaign budget
          budgetTotal: Number(formCampaign.budget || 0),
        };

        // Add line item updates if there are any
        if (lineItemUpdates.length > 0) {
          campaignUpdateData.lineItems = {
            update: lineItemUpdates
          };
        }

        // Add campaign update (includes budget even if no line items changed)
        campaignUpdates.push({
          where: { id: existingCampaign.id },
          data: campaignUpdateData
        });
      });

      // Only proceed if there are changes to save
      if (campaignUpdates.length === 0) {
        message.destroy();
        message.info('No changes to save');
        return;
      }

      const updateData = {
        campaigns: {
          update: campaignUpdates
        }
      };

      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: updateData,
        },
      });

      message.destroy();
      message.success('Budget changes saved successfully');
    } catch (error) {
      message.destroy();
      const errorMessages = getCampaignOrderErrorMessage(error);
      message.error(errorMessages[0] || 'Failed to save changes', 5);
      console.error('Save error:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const handleSubmitRequest = async () => {
    if (!currentCampaignOrder?.id) {
      message.warning('No campaign order to submit');
      return;
    }

    try {
      if (!isMountedRef.current) return;
      setLoading(true);

      message.loading('Submitting request...', 0);

      // Determine the target status based on current status
      const currentStatus = currentCampaignOrder?.status;
      let status = 'PENDING';  // Default for DRAFT and other statuses

      if (currentStatus === 'LIVE') {
        status = 'LIVE_PENDING';
      } else if (currentStatus === 'PAUSED') {
        status = 'PAUSED_PENDING';
      }

      // Build update payload that includes both status and any pending budget changes
      const formCampaigns = formValues.campaigns || [];
      const existingCampaigns = currentCampaignOrder?.campaigns || [];

      // Customers don't manage the delivery flag — every line item in their
      // request is meant to deliver. Auto-mark valid items at submit (Ad Ops
      // mark manually and are untouched). Reference items (explicitly unmarked
      // by a blueprint apply) are never flipped.
      if (!isAdOps) {
        const savedPricingModel = currentCampaignOrder?.pricingModel;
        const hideCpmFields = savedPricingModel
          ? savedPricingModel === 'Percentage'
          : isPercentageModel(selectedAdvertiser);

        const invalidItems = [];
        const idsToMark = [];
        formCampaigns.forEach((formCampaign) => {
          const { updatedLineItems, invalidItems: campaignInvalid } = markDeliverableLineItems(
            formCampaign.lineItems || [],
            hideCpmFields
          );
          campaignInvalid.forEach((item) => invalidItems.push(item));
          (formCampaign.lineItems || []).forEach((original, idx) => {
            if (original.id && original.markedForDelivery !== true && updatedLineItems[idx].markedForDelivery === true) {
              idsToMark.push(original.id);
            }
          });
        });

        if (invalidItems.length > 0) {
          const details = invalidItems
            .map((item) => `"${item.name || 'Untitled'}" (missing: ${item.missingFields.join(', ')})`)
            .join('; ');
          message.destroy();
          message.error(`Some line items aren't ready for delivery: ${details}`, 8);
          setLoading(false);
          return;
        }

        await Promise.all(
          idsToMark.map((lineItemId) =>
            updateLineItemMarkedForDelivery({
              variables: { lineItemId, data: { markedForDelivery: true } },
            })
          )
        );
      }

      const campaignUpdates = [];

      // Build campaign updates if there are changes
      if (existingCampaigns.length > 0 && formCampaigns.length > 0) {
        existingCampaigns.forEach((existingCampaign, campaignIndex) => {
          const formCampaign = formCampaigns[campaignIndex];

          if (!existingCampaign.id || !formCampaign) {
            return;
          }

          const lineItems = formCampaign.lineItems || [];

          const lineItemUpdates = lineItems
            .filter(lineItem => lineItem.id)
            .map(lineItem => ({
              where: { id: lineItem.id },
              data: {
                budgetTotal: Number(lineItem.budgetTotal || 0),
              }
            }));

          const campaignUpdateData = {
            budgetTotal: Number(formCampaign.budget || 0),
          };

          if (lineItemUpdates.length > 0) {
            campaignUpdateData.lineItems = {
              update: lineItemUpdates
            };
          }

          campaignUpdates.push({
            where: { id: existingCampaign.id },
            data: campaignUpdateData
          });
        });
      }

      // Build final update payload with status and campaign updates
      const updateData = {
        status,
      };

      if (campaignUpdates.length > 0) {
        updateData.campaigns = {
          update: campaignUpdates
        };
      }

      // Single mutation to update both status and budget changes
      await updateCampaignOrder({
        variables: {
          where: { id: currentCampaignOrder.id },
          data: updateData,
        },
      });

      message.destroy();
      message.success('Request submitted successfully');

      // Small delay before navigation to allow Apollo cache to settle
      // This prevents race conditions with queries on the next page
      await new Promise(resolve => setTimeout(resolve, 150));

      // Check if component is still mounted before navigation
      if (isMountedRef.current) {
        // Navigate to campaigns main page
        history.push('/campaigns/main/1');
      }
    } catch (error) {
      message.destroy();
      const errorMessages = getCampaignOrderErrorMessage(error);
      message.error(errorMessages[0] || 'Failed to submit request', 5);
      console.error('Submit request error:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <Col className="campaign-requester-sticky">
      <Card
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: advertiser ? 4 : 0,
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>
              SUMMARY {campaignName ? <small>- [{campaignName}] </small> : ''}
            </span>
            {currentCampaignOrder && (
              <StatusTag
                startDate={currentCampaignOrder.startDate || new Date().toString()}
                endDate={currentCampaignOrder.endDate || new Date().toString()}
                statusCampaign={currentCampaignOrder.status}
                attributionWindow={currentCampaignOrder.attributionWindow || 7}
                impressions={getServedImpressions(currentCampaignOrder)}
              />
            )}
          </div>
        }
        style={{ marginBottom: 20 }}
        className="campaign-requester-summary"
      >
        {advertiser && (
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #f0f0f0' }}>
            <Text>{advertiser.name}</Text>
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <Text>
            Take a moment to review the details of this request before submitting it.
          </Text>
        </div>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {canEdit ? (
            <>
              <Button
                type="default"
                block
                onClick={handleSave}
                loading={loading}
                disabled={loading}
              >
                Save
              </Button>

              <Button
                type="default"
                block
                onClick={handleMakeEdits}
                disabled={loading}
              >
                Make Edits
              </Button>

              <Button
                type="primary"
                block
                onClick={handleSubmitRequest}
                disabled={loading}
              >
                Submit Request
              </Button>
            </>
          ) : (
            <Text type="secondary">
              This campaign has been submitted for launch and can no longer be edited. Please
              contact your Ad Ops team for changes.
            </Text>
          )}
        </Space>
      </Card>
    </Col>
  );
};

export default CampaignRequestorReviewSummary;